import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    AuthService,
    DataSources,
    EmployeesClient, PermissionType,
    ShiftDetailsDto,
    UpdateShiftDetailsCommand
} from '@services';
import {
    DxSelectBoxModule,
    DxValidationGroupComponent,
    DxValidationGroupModule,
    DxValidatorModule
} from 'devextreme-angular';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DxiValidationRuleComponent } from "devextreme-angular/ui/nested";
import notify from 'devextreme/ui/notify';
import { PicturedItemSelectBoxComponent } from 'src/app/components';

@Component({
    selector: 'app-shift-details',
    templateUrl: './shift-details.component.html',
    standalone: true,
    imports: [
        CommonModule,
        DxSelectBoxModule,
        DxValidationGroupModule,
        DxValidatorModule,
        DxiValidationRuleComponent,
        PicturedItemSelectBoxComponent
    ]
})
export class ShiftDetailsComponent implements OnInit {
    private employeesClient = inject(EmployeesClient);
    private dataSources = inject(DataSources);
    protected authService = inject(AuthService);

    @Input() userId: string;
    @Input() forceReadOnly = false;
    @Output() isDataChanged = new EventEmitter<boolean>();

    @ViewChild('validationGroup', { static: false }) validationGroup: DxValidationGroupComponent;

    shiftDetails: ShiftDetailsDto;
    isLoading = false;

    protected shiftDataSource = this.dataSources.shiftOptions();
    protected supervisorDataSource = this.dataSources.supervisorOptions();

    ngOnInit(): void {
        this.loadData();
    }

    loadData() {
        if (!this.userId) return;
        this.isLoading = true;
        this.employeesClient.getShiftDetails(this.userId).subscribe({
            next: (data) => {
                this.shiftDetails = data;
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                notify('Failed to load shift details', 'error', 3000);
            }
        });
    }

    public save(): Observable<void> {
        if (this.isReadOnly) return of(void 0);
        if (!this.validationGroup) return of();

        const result = this.validationGroup.instance.validate();
        if (!result.isValid) {
            notify({ message: 'Please fix shift details errors', position: 'bottom center' }, 'error', 3000);
            return throwError(() => new Error('Validation failed'));
        }

        const command = new UpdateShiftDetailsCommand({
            userId: this.userId,
            shiftId: this.shiftDetails.shiftId,
            supervisorId: this.shiftDetails.supervisorId
        });

        return this.employeesClient.updateShiftDetails(command).pipe(
            catchError((err) => {
                const errors = err?.response?.errors || [{ message: 'Failed to update shift details' }];
                errors.forEach((errMsg: any) => {
                    notify({
                        message: errMsg.message || errMsg,
                        type: "error",
                        displayTime: 5000,
                    }, {
                        position: "bottom right",
                        direction: "up-push"
                    });
                });
                return throwError(() => err);
            })
        );
    }

    onFieldChange() {
        this.isDataChanged.emit(true);
    }

    get isReadOnly(): boolean {
        return this.forceReadOnly || !this.authService.canEditProfileSection(this.userId, PermissionType.EmployeeShiftDetailsOwn, PermissionType.EmployeeShiftDetailsOthers);
    }
}

